import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
} from '@angular/core';
import { COMMON_IMPORTS } from '../../common-imports';
import { ThemeService } from '../../Services/theme.service';
import { accentFromSeed } from '../../Utils/avatar-color.utils';
import { AvAvatarSize } from '../../Types/av-avatar-size.type';

@Component({
  selector: 'av-avatar',
  standalone: true,
  imports: [COMMON_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './av-avatar.component.html',
  styleUrls: ['../../../../../assets/theme/cutom/_av-avatar.scss'],
})
export class AvAvatarComponent {
  private readonly _theme = inject(ThemeService);

  readonly name = input.required<string>();
  readonly photoUrl = input<string | null>(null);
  readonly size = input<AvAvatarSize>('md');

  private readonly _imageFailed = linkedSignal<string | null, boolean>({
    source: this.photoUrl,
    computation: () => false,
  });

  readonly initials = computed<string>(() => {
    const words = (this.name() ?? '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return '';
    }
    const first = words[0].charAt(0);
    const second = words.length > 1 ? words[1].charAt(0) : '';
    return (first + second).toUpperCase();
  });

  readonly hasInitials = computed<boolean>(() => this.initials().length > 0);

  readonly showImage = computed<boolean>(
    () => !!this.photoUrl() && !this._imageFailed(),
  );

  readonly backgroundColor = computed<string | null>(() => {
    const seed = (this.name() ?? '').trim();
    if (!seed) {
      return null;
    }
    return accentFromSeed(seed, this._theme.mode() === 'dark');
  });

  readonly textColor = computed<string | null>(() => {
    const bg = this.backgroundColor();
    return bg ? this._contrastText(bg) : null;
  });

  onImageError(): void {
    this._imageFailed.set(true);
  }

  private _contrastText(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const lin = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return luminance > 0.55 ? '#1d1d1d' : '#ffffff';
  }
}
